import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { ShipmentByCustomerReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentByCustomer } from './exportShipmentReports';

export function ShipmentByCustomerPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [report, setReport] = useState<ShipmentByCustomerReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('customerName');

  const rows = useMemo(
    () =>
      report
        ? sortItems(report.data, sort, {
            customerName: (r) => r.customerName,
            fileCount: (r) => r.fileCount,
            gross: (r) => r.expenses.gross,
          })
        : [],
    [report, sort],
  );

  useEffect(() => {
    void api.getCustomerLookup({ active: 'true' }).then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentByCustomerReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          customerId: customerId ? Number(customerId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, customerId]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report || loading}
            onExcel={() => {
              if (!report) return;
              exportShipmentByCustomer({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (!report) return;
              exportShipmentByCustomer({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || rows.length === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <SortableTh sortKey="customerName" sort={sort} onSort={toggleSort}>
                  {t().common.customer}
                </SortableTh>
                <SortableTh sortKey="fileCount" sort={sort} onSort={toggleSort}>
                  {labels.fileCount}
                </SortableTh>
                <th className="px-4 py-3 font-semibold">{labels.openCount}</th>
                <th className="px-4 py-3 font-semibold">{labels.invoicedCount}</th>
                <SortableTh
                  sortKey="gross"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.expenseGross}
                </SortableTh>
                <th className="px-4 py-3 text-right font-semibold">
                  {labels.openBillable}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.customerId} className="border-b border-line/70">
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers/${row.customerId}`}
                      className="font-medium text-teal-700 hover:underline dark:text-teal-300"
                    >
                      {row.customerName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.fileCount}</td>
                  <td className="px-4 py-3 tabular-nums">{row.openCount}</td>
                  <td className="px-4 py-3 tabular-nums">{row.invoicedCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatMoney(row.expenses.gross)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatMoney(row.openBillable.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </PageShell>
  );
}
